import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../../api/axios';

export default function PriseCommande() {
  const { tableId } = useParams();
  const navigate = useNavigate();
  const [categories, setCategories] = useState([]);
  const [catActive, setCatActive] = useState(null);
  const [panier, setPanier] = useState([]);
  const [table, setTable] = useState(null);
  const [loading, setLoading] = useState(true);
  const [envoi, setEnvoi] = useState(false);
  const [note, setNote] = useState('');

  useEffect(() => {
    Promise.all([
      api.get('/articles'),
      api.get('/tables')
    ]).then(([articlesRes, tablesRes]) => {
      setCategories(articlesRes.data);
      setCatActive(articlesRes.data[0]?.id);
      // Trouver la table
      const toutesLesTables = tablesRes.data.flatMap(z => z.tables);
      setTable(toutesLesTables.find(t => t.id === parseInt(tableId)));
      setLoading(false);
    });
  }, [tableId]);

  const ajouterAuPanier = (article) => {
    setPanier(prev => {
      const exist = prev.find(p => p.article_id === article.id);
      if (exist) {
        return prev.map(p =>
          p.article_id === article.id
            ? { ...p, quantite: p.quantite + 1, sous_total: (p.quantite + 1) * p.prix_unitaire }
            : p
        );
      }
      return [...prev, {
        article_id: article.id,
        nom: article.nom,
        quantite: 1,
        prix_unitaire: article.prix_unitaire,
        sous_total: article.prix_unitaire,
        note: ''
      }];
    });
  };

  const retirerDuPanier = (article_id) => {
    setPanier(prev => {
      const exist = prev.find(p => p.article_id === article_id);
      if (exist?.quantite === 1) return prev.filter(p => p.article_id !== article_id);
      return prev.map(p =>
        p.article_id === article_id
          ? { ...p, quantite: p.quantite - 1, sous_total: (p.quantite - 1) * p.prix_unitaire }
          : p
      );
    });
  };

  const total = panier.reduce((sum, p) => sum + p.sous_total, 0);

  const envoyerCommande = async () => {
    if (panier.length === 0) return;
    setEnvoi(true);
    try {
      await api.post('/commandes', {
        table_id: parseInt(tableId),
        note: note,
        lignes: panier.map(p => ({
          article_id: p.article_id,
          quantite: p.quantite,
          note: p.note
        }))
      });
      navigate('/serveur');
    } catch (err) {
      alert('Erreur lors de l\'envoi');
      setEnvoi(false);
    }
  };

  const catActuelle = categories.find(c => c.id === catActive);

  if (loading) return (
    <div className="min-h-screen bg-gray-900 flex items-center justify-center">
      <div className="text-white text-2xl">Chargement...</div>
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-900 text-white flex flex-col">

      {/* Header */}
      <div className="bg-gray-800 px-4 py-3 flex justify-between items-center">
        <button onClick={() => navigate('/serveur')} className="text-blue-400 text-sm">
          ← Retour
        </button>
        <h1 className="text-lg font-bold">Table {table?.numero}</h1>
        <span className="text-gray-400 text-sm">{panier.length} article(s)</span>
      </div>

      {/* Catégories */}
      <div className="flex overflow-x-auto bg-gray-800 border-t border-gray-700 px-2">
        {categories.map((cat) => (
          <button
            key={cat.id}
            onClick={() => setCatActive(cat.id)}
            className={`px-4 py-3 text-sm font-medium whitespace-nowrap border-b-2 transition ${
              catActive === cat.id
                ? 'border-blue-500 text-blue-400'
                : 'border-transparent text-gray-400 hover:text-white'
            }`}
          >
            {cat.nom}
          </button>
        ))}
      </div>

      {/* Contenu principal */}
      <div className="flex flex-1 overflow-hidden">

        {/* Articles */}
        <div className="flex-1 overflow-y-auto p-3 grid grid-cols-2 gap-2 content-start">
          {catActuelle?.articles?.map((article) => {
            const qte = panier.find(p => p.article_id === article.id)?.quantite || 0;
            return (
              <div
                key={article.id}
                className={`rounded-xl p-3 cursor-pointer transition ${
                  qte > 0 ? 'bg-blue-700' : 'bg-gray-700 hover:bg-gray-600'
                }`}
                onClick={() => ajouterAuPanier(article)}
              >
                <div className="text-sm font-medium leading-tight">{article.nom}</div>
                <div className="text-blue-300 text-sm mt-1">
                  {article.prix_unitaire.toLocaleString()} Ar
                </div>
                {qte > 0 && (
                  <div className="flex items-center justify-between mt-2">
                    <button
                      onClick={(e) => { e.stopPropagation(); retirerDuPanier(article.id); }}
                      className="bg-red-500 w-6 h-6 rounded-full text-white font-bold text-sm"
                    >
                      -
                    </button>
                    <span className="font-bold">{qte}</span>
                    <button
                      onClick={(e) => { e.stopPropagation(); ajouterAuPanier(article); }}
                      className="bg-green-500 w-6 h-6 rounded-full text-white font-bold text-sm"
                    >
                      +
                    </button>
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Panier */}
        <div className="w-48 bg-gray-800 flex flex-col border-l border-gray-700">
          <div className="p-3 border-b border-gray-700">
            <h2 className="font-bold text-sm">Commande</h2>
          </div>
          <div className="flex-1 overflow-y-auto p-2 space-y-1">
            {panier.length === 0 ? (
              <p className="text-gray-500 text-xs text-center mt-4">Panier vide</p>
            ) : (
              panier.map((item) => (
                <div key={item.article_id} className="bg-gray-700 rounded-lg p-2 text-xs">
                  <div className="font-medium leading-tight">{item.nom}</div>
                  <div className="flex justify-between mt-1 text-gray-300">
                    <span>x{item.quantite}</span>
                    <span>{item.sous_total.toLocaleString()} Ar</span>
                  </div>
                </div>
              ))
            )}
          </div>

          {/* Note */}
          <div className="p-2 border-t border-gray-700">
            <textarea
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="Note..."
              className="w-full bg-gray-700 text-white text-xs rounded p-2 resize-none h-12"
            />
          </div>

          {/* Total + Envoyer */}
          <div className="p-3 border-t border-gray-700">
            <div className="text-sm font-bold mb-2">
              Total: {total.toLocaleString()} Ar
            </div>
            <button
              onClick={envoyerCommande}
              disabled={panier.length === 0 || envoi}
              className="w-full bg-green-600 text-white py-3 rounded-xl font-bold text-sm hover:bg-green-700 disabled:opacity-50 transition"
            >
              {envoi ? 'Envoi...' : '✓ ENVOYER'}
            </button>
          </div>
        </div>

      </div>
    </div>
  );
}